import type { ComponentType } from "react";
import {
  createBrowserRouter,
  replace,
  type LoaderFunctionArgs,
  type RouteObject,
} from "react-router";
import { legacyRedirects, legacyTarget } from "./legacyRedirects";
import { RootLayout } from "./RootLayout";
import { AppSplash, PageError, RootError } from "./RouteError";
import type { RouteHandle, ScreenId } from "./routes";

type LazyRoute = NonNullable<RouteObject["lazy"]>;

/**
 * A route whose component is loaded on first visit, in its own chunk. React Router clears the lazy object's keys as
 * they load, so every route needs its own object: never share one between routes or routers.
 */
function lazyComponent(load: () => Promise<ComponentType>): LazyRoute {
  return { Component: load };
}

function handle(screen: ScreenId): RouteHandle {
  return { screen };
}

const load = {
  home: () => import("@/pages/HomePage").then((module) => module.HomePage),
  login: () => import("@/pages/LoginPage").then((module) => module.LoginPage),
  logout: () =>
    import("@/pages/LogoutPage").then((module) => module.LogoutPage),
  notFound: () =>
    import("@/pages/NotFoundPage").then((module) => module.NotFoundPage),
  groups: () =>
    import("@/pages/groups/GroupsPage").then((module) => module.GroupsPage),
  group: () =>
    import("@/pages/groups/GroupPage").then((module) => module.GroupPage),
  newGroup: () =>
    import("@/pages/groups/NewGroupPage").then((module) => module.NewGroupPage),
  symbols: () =>
    import("@/pages/symbols/SymbolsPage").then((module) => module.SymbolsPage),
  botOrders: () =>
    import("@/pages/bot/BotOrdersPage").then((module) => module.BotOrdersPage),
  botOrder: () =>
    import("@/pages/bot/BotOrderPage").then((module) => module.BotOrderPage),
  botHistory: () =>
    import("@/pages/bot/BotHistoryPage").then(
      (module) => module.BotHistoryPage,
    ),
  klines: () =>
    import("@/pages/charts/KlinesPage").then((module) => module.KlinesPage),
  lines: () =>
    import("@/pages/charts/LinesPage").then((module) => module.LinesPage),
  lineTrades: () =>
    import("@/pages/charts/LineTradesPage").then(
      (module) => module.LineTradesPage,
    ),
  p2p: () => import("@/pages/p2p/P2PPage").then((module) => module.P2PPage),
  accounts: () =>
    import("@/pages/accounts/AccountsPage").then(
      (module) => module.AccountsPage,
    ),
  requireSession: () =>
    import("./RequireSession").then((module) => module.RequireSession),
  appLayout: () => import("./AppLayout").then((module) => module.AppLayout),
  marketGate: () => import("./MarketGate").then((module) => module.MarketGate),
};

/** A screen route: its component, loaded lazily, and its handle for the title. */
function screen(
  path: string | undefined,
  component: () => Promise<ComponentType>,
  id: ScreenId,
): RouteObject {
  const route = { lazy: lazyComponent(component), handle: handle(id) };
  return path === undefined ? { index: true, ...route } : { path, ...route };
}

/** Replaced by `false` in production builds, so the page and its chunk are left out entirely. */
function devRoutes(): RouteObject[] {
  return import.meta.env.DEV
    ? [
        {
          path: "dev/blocks",
          lazy: () => import("@/pages/dev/BlocksPage"),
          handle: handle("blocks"),
        },
      ]
    : [];
}

function legacyLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  return replace(legacyTarget(url.pathname, url.search) ?? "/");
}

/**
 * The route tree. Old paths redirect first; the sign-in pages are public; everything else needs a session and sits in
 * the shell, whose inner error boundary keeps the navigation usable when a page fails.
 */
export function createAppRoutes({
  dev = import.meta.env.DEV,
}: { dev?: boolean } = {}): RouteObject[] {
  return [
    {
      id: "root",
      Component: RootLayout,
      ErrorBoundary: RootError,
      HydrateFallback: AppSplash,
      children: [
        ...legacyRedirects.map(({ from }): RouteObject => ({
          path: from,
          loader: legacyLoader,
        })),
        screen("login", load.login, "login"),
        screen("logout", load.logout, "logout"),
        {
          lazy: lazyComponent(load.requireSession),
          children: [
            {
              lazy: lazyComponent(load.appLayout),
              children: [
                {
                  ErrorBoundary: PageError,
                  children: [
                    screen(undefined, load.home, "home"),
                    {
                      path: "groups/:market",
                      lazy: lazyComponent(load.marketGate),
                      children: [
                        screen(undefined, load.groups, "groups"),
                        screen("new", load.newGroup, "newGroup"),
                        screen(":groupId", load.group, "group"),
                      ],
                    },
                    screen("line-trades", load.lineTrades, "lineTrades"),
                    {
                      path: "bot/:market",
                      lazy: lazyComponent(load.marketGate),
                      children: [
                        screen(undefined, load.notFound, "notFound"),
                        screen("orders", load.botOrders, "botOrders"),
                        screen("orders/:symbol", load.botOrder, "botOrder"),
                        screen("history", load.botHistory, "botHistory"),
                      ],
                    },
                    {
                      path: "symbols/:market",
                      lazy: lazyComponent(load.marketGate),
                      children: [screen(undefined, load.symbols, "symbols")],
                    },
                    screen("charts/klines", load.klines, "klines"),
                    screen("charts/lines", load.lines, "lines"),
                    screen("p2p", load.p2p, "p2p"),
                    screen("accounts", load.accounts, "accounts"),
                    ...(dev ? devRoutes() : []),
                    screen("*", load.notFound, "notFound"),
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];
}

export function createAppRouter() {
  return createBrowserRouter(createAppRoutes());
}
